import React, { useState, useEffect } from 'react'
import {Sparkles, X, ArrowRight, Gift, Clock, CheckCircle} from 'lucide-react'

const STORAGE_KEY = 'tpc_exit_intent_shown'

const ExitIntentPopup: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasShown, setHasShown] = useState(false)

  useEffect(() => {
    // Check session storage to avoid showing repeatedly in same session
    const alreadyShown = sessionStorage.getItem(STORAGE_KEY)
    if (alreadyShown === '1') {
      setHasShown(true)
      return
    }

    let timeoutFallback: number | undefined

    const triggerPopup = () => {
      if (hasShown) return
      setIsOpen(true)
      setHasShown(true)
      sessionStorage.setItem(STORAGE_KEY, '1')
      // Track engagement event
      if (typeof window !== 'undefined') {
        console.log('engagement_event:', { type: 'exit_intent_shown', timestamp: Date.now() })
      }
    }

    // Desktop: detect mouse leaving viewport from top
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShown) {
        triggerPopup()
      }
    }

    // Mobile fallback: trigger after 45 seconds of activity
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)
    if (isMobile) {
      timeoutFallback = window.setTimeout(() => {
        if (!hasShown && document.visibilityState === 'visible') {
          triggerPopup()
        }
      }, 45000)
    } else {
      document.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave)
      if (timeoutFallback) clearTimeout(timeoutFallback)
    }
  }, [hasShown])

  const handleClose = () => {
    setIsOpen(false)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
      <div
        className="relative max-w-lg w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl shadow-cyan-500/20 border border-cyan-500/30 overflow-hidden"
        style={{ animation: 'slideUp 0.4s ease-out' }}
      >
        {/* Decorative gradient blob */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-cyan-500/30 to-emerald-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-gradient-to-tr from-amber-500/20 to-orange-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative p-8 md:p-10">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full mb-5">
            <Gift className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-xs font-semibold uppercase tracking-wider">Wait! Before You Go</span>
          </div>

          {/* Headline */}
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
            Get Your <span className="bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">Free AI Readiness</span> Assessment
          </h2>

          <p className="text-gray-300 text-base md:text-lg mb-6 leading-relaxed">
            Discover how AI can transform your business in just 5 minutes. No commitment, instant insights.
          </p>

          {/* Benefits list */}
          <div className="space-y-2.5 mb-7">
            {[
              'Personalized AI strategy recommendations',
              'Industry-specific insights & benchmarks',
              'Actionable next steps — delivered instantly'
            ].map((benefit) => (
              <div key={benefit} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-200 text-sm md:text-base">{benefit}</span>
              </div>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://ai.triangleprojectconsultants.com"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                console.log('engagement_event:', { type: 'exit_intent_cta_click', timestamp: Date.now() })
                handleClose()
              }}
              className="group flex-1 flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900 font-bold rounded-xl shadow-lg shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-[1.02]"
            >
              <Sparkles className="w-5 h-5" />
              <span>Start Free Assessment</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <button
              onClick={handleClose}
              className="px-6 py-3.5 text-gray-400 hover:text-white font-medium transition"
            >
              No thanks
            </button>
          </div>

          {/* Trust line */}
          <div className="flex items-center justify-center gap-2 mt-5 text-gray-500 text-xs">
            <Clock className="w-3.5 h-3.5" />
            <span>Takes less than 5 minutes • 100% Free • No credit card required</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fade-in { animation: fadeIn 0.3s ease-out; }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  )
}

export default ExitIntentPopup
